import { Badge, GridCellText, Icon, type TableColumn, Tooltip } from '@civilgrid/ui/components';
import { OPERATOR_TYPES } from '@civilgrid/ui/utils';
import type { CellContext } from '@tanstack/react-table';
import { DateTime } from 'luxon';
import { civilSearchTierLabels } from '~/common/dataRequestUtils';
import { type Agency, CivilSearchTier } from '~/common/types';
import AssetType, { ASSET_TYPES } from '../common/AssetType';

export function getAgencyRowLabel(agency: Pick<Agency, 'id' | 'agencyName' | 'type' | 'state'>): string {
  const details = [agency.type, agency.state, agency.agencyName ? agency.id : undefined].filter(Boolean).join(', ');
  const name = agency.agencyName || agency.id;
  return details ? `agency ${name} (${details})` : `agency ${name}`;
}

const formatMergedAt = (iso: string) => {
  const dt = DateTime.fromISO(iso);
  if (!dt.isValid) return new Date(iso).toLocaleDateString();
  return dt.toLocaleString(DateTime.DATE_MED);
};

const MergedBadge = ({ mergedInto, testId }: { mergedInto: NonNullable<Agency['mergedInto']>; testId: string }) => (
  <Tooltip
    contentChildren={
      <span className="text-xs text-white whitespace-pre-wrap max-w-xs block">
        {`Merged into ${mergedInto.targetAgencyName} on ${formatMergedAt(mergedInto.mergedAt)} by ${mergedInto.mergedByName}`}
      </span>
    }
    data-testid={testId}
  >
    <Badge color="grey" kind="outline">
      Merged
    </Badge>
  </Tooltip>
);

const renderIsPublished = (cell: CellContext<Agency, unknown>) => {
  const isChecked = cell.getValue<boolean>();

  if (isChecked) {
    return (
      <div className="flex w-full items-center justify-center">
        <span className="text-white bg-green-50 rounded-full p-0.5">
          <Icon icon="check" ariaLabel="Yes" />
        </span>
      </div>
    );
  }

  return <span className="sr-only">No</span>;
};

export const columns: TableColumn<Agency>[] = [
  {
    id: 'agencies.agencyName',
    header: 'Agency Name',
    accessorKey: 'agencyName',
    size: 300,
    cell: ({ row, getValue }) => {
      const value = getValue<string>();
      const { id, mergedInto } = row.original;
      // A merged-away agency opens its own page only with `?showMerged`; the details page otherwise redirects to the parent.
      const link = `/admin/civil-search/agencies/${id}/general${mergedInto ? '?showMerged=true' : ''}`;
      return (
        <GridCellText>
          <span className="flex items-center gap-2 min-w-0">
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="truncate text-primary-dark underline"
              data-testid={`agency-name-link-${id}`}
              title={value}
            >
              {value}
            </a>
          </span>
        </GridCellText>
      );
    },
    meta: {
      filterLabel: 'Agency Name',
      filterOperatorTypeConfig: OPERATOR_TYPES.TEXT,
    },
  },
  {
    id: 'agencies.merged',
    header: 'Merged',
    size: 120,
    accessorFn: (a) => (a.mergedInto ? 'Merged' : ''),
    // badge denotes an agency that has been merged into anoter agency -> tooltip specifies the "parent" agency
    // merge badge moved from agency named column to its own column for filtering
    // badge logic remains the same
    cell: ({ row }) => {
      const { mergedInto, id } = row.original;
      return (
        <GridCellText>
          {mergedInto ? <MergedBadge mergedInto={mergedInto} testId={`agency-merged-cell-badge-${id}`} /> : ''}
        </GridCellText>
      );
    },
    meta: {
      filterLabel: 'Merged',
      filterOperatorTypeConfig: OPERATOR_TYPES.BOOLEAN,
      filterValueConfig: { type: 'boolean' },
    },
  },
  {
    id: 'agencies.internalIds',
    header: 'Internal IDs',
    accessorKey: 'internalIds',
    size: 220,
    cell: ({ getValue, row }) => (
      <GridCellText>
        <span data-testid={`agency-internal-ids-${row.original.id}`} className="truncate" title={getValue<string>()}>
          {getValue<string>()}
        </span>
      </GridCellText>
    ),
    meta: {
      filterLabel: 'Internal IDs',
      // An agency owns a SET of internal ids (one agencyGeometry row per source feature), so the
      // API resolves each operator against the individual ids: Equals means "has this id", Does
      // Not Equal means "has no such id". Sorting is by the joined cell.
      filterOperatorTypeConfig: OPERATOR_TYPES.TEXT,
    },
  },
  {
    id: 'agencies.isPublished',
    header: 'Published',
    accessorKey: 'isPublished',
    size: 120,
    cell: renderIsPublished,
    meta: {
      filterLabel: 'Published',
      filterOperatorTypeConfig: OPERATOR_TYPES.BOOLEAN,
      filterValueConfig: { type: 'boolean' },
    },
  },
  {
    id: 'agencies.state',
    header: 'State',
    accessorKey: 'state',
    size: 90,
    cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
    meta: {
      filterLabel: 'State',
      filterOperatorTypeConfig: OPERATOR_TYPES.TEXT,
    },
  },
  {
    id: 'agencies.civilSearchTier',
    header: 'CivilSearch Tier',
    accessorKey: 'civilSearchTier',
    cell: ({ getValue }) => <GridCellText>{civilSearchTierLabels[getValue<CivilSearchTier>()] ?? ''}</GridCellText>,
    size: 150,
    meta: {
      filterLabel: 'CivilSearch Tier',
      filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
      filterValueConfig: {
        type: 'select',
        options: Object.values(CivilSearchTier).map((value) => ({
          value,
          label: civilSearchTierLabels[value],
        })),
      },
    },
  },
  {
    id: 'agencies.type',
    header: 'Type',
    accessorKey: 'type',
    minSize: 200,
    cell: ({ getValue }) => (
      <GridCellText>
        <AssetType type={getValue<string>()} />
      </GridCellText>
    ),
    meta: {
      filterLabel: 'Type',
      filterOperatorTypeConfig: OPERATOR_TYPES.EXACT,
      filterValueConfig: {
        type: 'select',
        options: ASSET_TYPES.map((type) => ({ value: type, label: type })),
      },
    },
  },
  {
    id: 'agencies.lastRefresh',
    header: 'Last Refresh',
    size: 125,
    accessorFn: (a) => a.lastRefresh?.toLocaleString() || '',
    cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
    meta: {
      filterLabel: 'Last Refresh',
      filterOperatorTypeConfig: OPERATOR_TYPES.DATE,
      filterValueConfig: { type: 'date' },
    },
  },
  {
    id: 'agencies.restrictions.urban',
    header: 'Urban Restrictions (ac)',
    accessorFn: (a) => a.restrictions?.urban || '',
    size: 200,
    enableSorting: false,
    cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
  },
  {
    id: 'agencies.restrictions.rural',
    header: 'Rural Restrictions (ac)',
    accessorFn: (a) => a.restrictions?.rural || '',
    size: 200,
    enableSorting: false,
    cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
  },
  {
    id: 'agencies.defaultCivilSearchAssignee.email',
    header: 'Default Assignee',
    accessorFn: (a) => a.defaultCivilSearchAssignee?.email ?? '',
    size: 200,
    cell: ({ getValue }) => <GridCellText>{getValue<string>()}</GridCellText>,
    meta: {
      filterLabel: 'Default Assignee',
      filterOperatorTypeConfig: OPERATOR_TYPES.TEXT,
    },
  },
  {
    id: 'agencies.id',
    header: 'Database ID',
    accessorKey: 'id',
    size: 160,
    enableSorting: false,
    // Hidden by default (meta.defaultVisible: false) — this column exists to expose the
    // "Database ID" filter option, not to add a raw-UUID column to the default grid.
    cell: ({ getValue, row }) => (
      <GridCellText>
        <span data-testid={`agency-database-id-${row.original.id}`} className="truncate" title={getValue<string>()}>
          {getValue<string>()}
        </span>
      </GridCellText>
    ),
    meta: {
      defaultVisible: false,
      filterLabel: 'Database ID',
      // like/nlike only — see filtersToSql: eq/neq on the uuid column crash on non-UUID input.
      filterOperatorTypeConfig: [
        { id: 'like', label: 'Includes' },
        { id: 'nlike', label: 'Does Not Include' },
      ],
    },
  },
];
